import { useCallback, useEffect, useReducer, useRef } from 'react';
import { isDialogOpen } from '../components/ui/useDialogDismiss';

/**
 * D119.11: undo / redo for an unsaved draft the page holds in state ("like Excel").
 *
 * The page keeps owning the draft (`useState`); this hook only remembers the drafts that came before.
 * Route every USER edit through `edit(updater, key?)` instead of calling `setDraft` directly. Server
 * copies adopted on load / Save / refresh are not edits: call `clear()` there.
 *
 * - `undo` puts back the previous draft exactly; `redo` re-applies; a new edit drops the redo branch.
 * - Capped (default 50 steps, oldest dropped).
 * - An edit that changes nothing is not a step (the updater returns its input, or the same value).
 * - `key` folds a burst into one step: edits with the same key less than `burstMs` apart (typing in one
 *   field) undo together. No key = always its own step.
 * - Ctrl/Cmd+Z undoes, Ctrl/Cmd+Y and Ctrl/Cmd+Shift+Z redo, except while focus is in a text field,
 *   so the browser's own field undo keeps working there. The shortcut listens only while `enabled`
 *   (the caller says where it applies: the open line drawer) and never while a dialog is open; the
 *   caller's own Undo / Redo buttons are not affected by either.
 */
export const DRAFT_HISTORY_LIMIT = 50;
export const DRAFT_BURST_MS = 1000;

const NON_TEXT_INPUT_TYPES = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'file', 'color', 'image']);

export function isTextEntryTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName.toUpperCase();
  if (tag === 'INPUT') return !NON_TEXT_INPUT_TYPES.has(String((el as HTMLInputElement).type || 'text').toLowerCase());
  return tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true;
}

type Update<T> = T | ((prev: T) => T);

export function useDraftHistory<T>(
  draft: T | null,
  setDraft: (next: T) => void,
  opts: { limit?: number; burstMs?: number; enabled?: boolean } = {}
) {
  const { limit = DRAFT_HISTORY_LIMIT, burstMs = DRAFT_BURST_MS, enabled = true } = opts;
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  // The newest draft this hook knows of: an edit made before React re-renders (two in one event) builds on it.
  const latest = useRef<T | null>(draft);
  latest.current = draft;
  const stacks = useRef<{ past: T[]; future: T[]; lastKey: string | null; lastAt: number }>({ past: [], future: [], lastKey: null, lastAt: 0 });

  const edit = useCallback(
    (update: Update<T>, key?: string) => {
      const prev = latest.current;
      if (prev == null) return;
      const next = typeof update === 'function' ? (update as (p: T) => T)(prev) : update;
      if (next === prev || JSON.stringify(next) === JSON.stringify(prev)) return;
      const s = stacks.current;
      const now = Date.now();
      const sameBurst = key != null && key === s.lastKey && now - s.lastAt < burstMs && s.past.length > 0;
      if (!sameBurst) {
        s.past.push(prev);
        if (s.past.length > limit) s.past.shift();
      }
      s.future = [];
      s.lastKey = key ?? null;
      s.lastAt = now;
      latest.current = next;
      setDraft(next);
      rerender();
    },
    [setDraft, limit, burstMs]
  );

  const undo = useCallback(() => {
    const s = stacks.current;
    const current = latest.current;
    if (current == null || !s.past.length) return;
    const target = s.past.pop() as T;
    s.future.push(current);
    s.lastKey = null;
    latest.current = target;
    setDraft(target);
    rerender();
  }, [setDraft]);

  const redo = useCallback(() => {
    const s = stacks.current;
    const current = latest.current;
    if (current == null || !s.future.length) return;
    const target = s.future.pop() as T;
    s.past.push(current);
    s.lastKey = null;
    latest.current = target;
    setDraft(target);
    rerender();
  }, [setDraft]);

  const clear = useCallback(() => {
    stacks.current = { past: [], future: [], lastKey: null, lastAt: 0 };
    rerender();
  }, []);

  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  undoRef.current = undo;
  redoRef.current = redo;
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e: KeyboardEvent) => {
      // A dialog open (a confirm the caller is awaiting) must not have the draft change under it.
      if (!(e.ctrlKey || e.metaKey) || e.altKey || isTextEntryTarget(e.target) || isDialogOpen()) return;
      const k = String(e.key).toLowerCase();
      const isRedo = k === 'y' || (k === 'z' && e.shiftKey);
      const isUndo = k === 'z' && !e.shiftKey;
      if (!isUndo && !isRedo) return;
      e.preventDefault();
      (isUndo ? undoRef : redoRef).current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled]);

  return { edit, undo, redo, clear, canUndo: stacks.current.past.length > 0, canRedo: stacks.current.future.length > 0 };
}
